import { FC } from 'react'

import { StarRatingAnalyticsData, StarRatingPopupContextProvider } from './StarRatingPopup.context'

import { StarRatingEntry } from 'models/assignment'
import { StarRatingPopupController } from './StarRatingPopup.controller'

export interface StarRatingPopupProps {
  /** Whether the popup is open */
  isOpen: boolean
  /** Close handler */
  onClose: () => void
  /** Assignment ID to submit rating for */
  assignmentId: string
  /** Whether the assignment has a creative (shows CREATIVE dimension) */
  hasCreative: boolean
  /** Creative display name (for subtitle and favorite prompt) */
  creativeName?: string
  /** Creative avatar URL */
  creativeAvatarUrl?: string
  /** Pre-filled ratings for "Update" mode */
  initialRatings?: StarRatingEntry[]
  /** BE-reported favourite-CT flag; used when initialRatings has no CREATIVE entry */
  initialIsFavouriteCT?: boolean
  /** Callback after successful submission + user closes the thank-you step. Receives the submitted entries and the avg rating from the response. */
  onComplete?: (entries: StarRatingEntry[], avgRating?: number | null) => void
  /** Additional analytics context for the star_rating_* Mixpanel event */
  analyticsData?: StarRatingAnalyticsData
}

/**
 * Reusable star rating popup.
 * Context-independent - receives everything via props.
 * Can be used in download flow (with downloadState) or standalone (order list).
 *
 * @example
 * // Standalone (order list)
 * <StarRatingPopup
 *   isOpen={isOpen}
 *   onClose={close}
 *   assignmentId="123"
 *   hasCreative={true}
 *   creativeName="John Doe"
 *   onComplete={() => refetch()}
 * />
 *
 * // Download flow
 * <StarRatingPopup
 *   isOpen={isOpen}
 *   onClose={close}
 *   assignmentId="123"
 *   hasCreative={true}
 *   downloadState={{ status: 'preparing' }}
 *   onComplete={handlePostRating}
 * />
 */
export const StarRatingPopup: FC<StarRatingPopupProps> = ({
  isOpen,
  onClose,
  assignmentId,
  hasCreative,
  creativeName,
  creativeAvatarUrl,
  initialRatings,
  initialIsFavouriteCT,
  onComplete,
  analyticsData
}) => (
  <StarRatingPopupContextProvider
    assignmentId={assignmentId}
    hasCreative={hasCreative}
    isOpen={isOpen}
    creativeName={creativeName}
    creativeAvatarUrl={creativeAvatarUrl}
    initialRatings={initialRatings}
    initialIsFavouriteCT={initialIsFavouriteCT}
    onComplete={onComplete}
    analyticsData={analyticsData}
  >
    <StarRatingPopupController
      isOpen={isOpen}
      onClose={onClose}
    />
  </StarRatingPopupContextProvider>
)
